import { api } from '@/lib/api';

export type InstrumentScope = 'tbm' | 'individual' | 'composite';

export interface InstrumentListItem {
  id: string;
  code: string;
  scope: InstrumentScope;
  name: string;
  organization: string | null;
  description: string | null;
  versionId: string;
  version: number;
  status: 'draft' | 'published' | 'archived';
  publishedAt: string | null;
  indicatorCount: number;
}

export interface ScaleCriterion {
  score: number;
  description: string;
}

export interface MovItem {
  label: string;
  requirement: 'optional' | 'required';
  needsAttachment: boolean;
}

export interface Indicator {
  id: string;
  number: number;
  name: string;
  description: string | null;
  weight: number;
  inputType: 'scale' | 'percentage_auto';
  priorityYear: number | null;
  needsVerification: boolean;
  criteria: ScaleCriterion[];
  mov: MovItem[];
}

export interface Dimension {
  id: string;
  code: string;
  name: string;
  journey: string | null;
  /** Dihitung server dari Σ bobot indikator — tidak disimpan sebagai kolom. */
  weight: number;
  indicators: Indicator[];
}

export interface Section {
  id: string;
  code: string;
  name: string;
  subject: 'tbm' | 'individual';
  entryMode: 'single' | 'matrix';
  weightInFinal: number;
  weight: number;
  dimensions: Dimension[];
}

export interface Band {
  gt: number | null;
  name: string;
  nameIndividual: string | null;
  display: string | null;
  characteristic: string | null;
}


export interface VersionSummary {
  id: string;
  version: number;
  status: 'draft' | 'published' | 'archived';
  publishedAt: string | null;
}

export interface InstrumentDetail {
  id: string;
  code: string;
  scope: InstrumentScope;
  name: string;
  organization: string | null;
  description: string | null;
  versions: VersionSummary[];
  version: {
    id: string;
    version: number;
    status: 'draft' | 'published' | 'archived';
    evidenceLabel: string | null;
    evidenceHint: string | null;
    scoringFormula: string | null;
    scaleLabels: { score: number; label: string }[];
    bands: Band[];
    meta: {
      fill_rules?: string[] | null;
      notes?: { key: string; text: string }[];
      recommendation_rules?: string[] | null;
      source?: string | null;
      rounding_rule?: string | null;
    };
  };
  sections: Section[];
}

export interface DimensionInput {
  code: string;
  name: string;
  journey?: string | null;
}

export interface IndicatorInput {
  number: number;
  name: string;
  description: string | null;
  weight: number;
  priorityYear: number | null;
  criteria: { score: number; description: string }[];
  mov: { label: string; requirement: 'optional' | 'required' }[];
}

export const instrumentApi = {
  list: () => api.get<InstrumentListItem[]>('/instruments'),
  get: (id: string, version?: number) =>
    api.get<InstrumentDetail>(`/instruments/${id}${version ? `?versi=${version}` : ''}`),

  createVersion: (id: string) =>
    api.post<{ id: string; version: number }>(`/instruments/${id}/versions`),
  publishVersion: (id: string, versionId: string) =>
    api.post<{ version: number }>(`/instruments/${id}/versions/${versionId}/publish`),
  deleteVersion: (id: string, versionId: string) =>
    api.del(`/instruments/${id}/versions/${versionId}`),

  createDimension: (sectionId: string, body: DimensionInput) =>
    api.post(`/instruments/sections/${sectionId}/dimensions`, body),
  updateDimension: (id: string, body: Partial<DimensionInput>) =>
    api.patch(`/instruments/dimensions/${id}`, body),
  deleteDimension: (id: string) =>
    api.del<{ id: string; deletedIndicators: number }>(`/instruments/dimensions/${id}`),

  createIndicator: (dimensionId: string, body: IndicatorInput) =>
    api.post(`/instruments/dimensions/${dimensionId}/indicators`, body),
  updateIndicator: (id: string, body: IndicatorInput) =>
    api.patch(`/instruments/indicators/${id}`, body),
  deleteIndicator: (id: string) => api.del(`/instruments/indicators/${id}`),

  saveBands: (instrumentId: string, bands: BandInput[]) =>
    api.put(`/instruments/${instrumentId}/bands`, { bands }),

};

export interface BandInput {
  /** null = kategori penampung terbawah. */
  gt: number | null;
  name: string;
  nameIndividual: string | null;
  characteristic: string | null;
}


export const SCOPE_LABEL: Record<InstrumentScope, string> = {
  tbm: 'TBM',
  individual: 'Individu PELITA SLI',
  composite: 'Komposit (PELITA SLI + TBM)',
};
